import axios, { AxiosHeaders, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { authActions } from 'features/Auth/authSlice';
import authStorage from 'features/Auth/authStorage';
import { appActions } from 'features/common/appSlice';
import { editModeActions } from 'features/common/editModeSlice';
import { StatusCodes } from 'http-status-codes';
import { store } from '../app/store';
import { isEditModeAPI } from './common';

const { dispatch } = store;
let openingReeditError = false;

const axiosClient = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Add a request interceptor
axiosClient.interceptors.request.use(
  function (config: InternalAxiosRequestConfig) {
    const href = new URL(window.location.href);
    const paramEditMode = href.searchParams.get('edit_mode');
    const paramDeliveryId = href.searchParams.get('delivery_id');

    const state = store.getState();
    const isEditMode = state.editMode.isEditMode || (paramEditMode === 'reedit' && paramDeliveryId != null);

    dispatch(appActions.showAppLoading());
    const token = authStorage.getAccessToken();
    config.headers = {
      ...config.headers,
      ...(token ? { Authorization: `Token ${token}` } : {}),
    } as AxiosHeaders;

    // add params edit_mode and delivery_id
    const url = new URL(config.url!, window.location.origin);
    if (isEditMode && isEditModeAPI(url.pathname)) {
      url.searchParams.set('edit_mode', isEditMode ? 'reedit' : '');
      url.searchParams.set('delivery_id', state.editMode.editModeData?.deliveryId.toString() || paramDeliveryId || '');
      config.url = url.pathname + url.search;
    }
    return config;
  },
  function (error) {
    dispatch(appActions.hideAppLoading());
    return Promise.reject(error);
  }
);

// Add a response interceptor
axiosClient.interceptors.response.use(
  function (response: AxiosResponse) {
    // Any status code that lie within the range of 2xx cause this function to trigger
    // Do something with response data
    dispatch(appActions.hideAppLoading());
    return response.data;
  },
  function (error) {
    const state = store.getState();
    dispatch(appActions.hideAppLoading());

    // Any status codes that falls outside the range of 2xx cause this function to trigger
    // Do something with response error
    // case 401
    if (error.response.status === StatusCodes.UNAUTHORIZED) {
      dispatch(editModeActions.disableEditMode());
      dispatch(authActions.actionLogoutSuccess());
    }

    // case 417 -> failed switch to reedit mode
    if (error.response.status === StatusCodes.EXPECTATION_FAILED) {
      const isOpen = state.editMode.isEditModeErrorOpen;
      if (!openingReeditError && !isOpen) {
        openingReeditError = true;
        store.dispatch(editModeActions.openEditModeError());
        queueMicrotask(() => {
          openingReeditError = false;
        });
      }
      return;
    }

    return Promise.reject(error);
  }
);

export default axiosClient;
